/**
 * 파일명 : usr0401m.jsx
 * 설명 : 상황판
 * 수정내역
 ************************************************
 * Version    수정일자      수정자    수정내용      
 ************************************************
 * 1.0       2026-03-19   한정호     최초작성
 * **/

/************************************************
 * Import 영역
 ************************************************/
/* React  */
import React, { useMemo, useCallback, useEffect } from 'react';

/* Reducer */
import { useAppSelector, useAppDispatch } from '../../../redux/hooks';
import { Usr0401mAction } from '../../../redux/slice/usr/usr0401m_slice';
import { DeptDataType, ProjectDataType, UserDataType, ProjectSummaryType } from '../../../redux/models/usr/usr0401m_model';

/* AntD  */
import { Layout, Typography, Row, Col, Space, Table, Modal, Button, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';

/* 업무  */
import * as commonProxy from 'libs/CommonProxy';

const { Content } = Layout;
const { Text } = Typography;

/************************************************
 * 부서 트리 → 테이블 구조 변환
 ************************************************/
const convertTreeToTable = (tree: any[], depth = 0, parentKey = ''): DeptDataType[] => {
    return tree.map((node: any, index: number) => {
        const uniqueKey = node.key || `${parentKey}_${depth}_${index}`;
        // 자식(하위) 부서가 있으면 재귀 호출로 먼저 처리
        const children = node.children ? convertTreeToTable(node.children, depth + 1, uniqueKey) : [];
        // 현재 노드의 기본값 초기화
        let { projectVacation = 0, waitingVacation = 0, vacationProject = 0, vacationWaiting = 0 } = node;
        // 자식 부서의 인원수 데이터를 부모(상위) 부서의 통계에 누적
        if (children.length > 0) {
            children.forEach((child: any) => {
                projectVacation += child.projectVacation || 0;
                waitingVacation += child.waitingVacation || 0;
                vacationProject += child.vacationProject || 0;
                vacationWaiting += child.vacationWaiting || 0;
            });
        }
        return {
            ...node,
            key: uniqueKey,
            dept: node.title,
            depth,
            total: node.total || 0,
            project: node.project || 0,
            waiting: node.waiting || 0,
            vacation: node.vacation || 0,
            projectVacation,
            waitingVacation,
            vacationProject,
            vacationWaiting,
            children: children.length > 0 ? children : undefined,
        };
    });
};

/************************************************
 * 날짜 형식 변환(YYYYMMDD -> YYYY-MM-DD)
 ************************************************/
const formatDate = (dateStr: string) => {
    if (!dateStr || dateStr.length !== 8) return dateStr;
    return `${dateStr.substring(0, 4)}-${dateStr.substring(4, 6)}-${dateStr.substring(6, 8)}`;
};

/************************************************
 * 사용자 목록 가져오기
 ************************************************/
const extractUserList = (responseData: any) => {
    return Array.isArray(responseData?.userList) 
        ? responseData.userList 
        : (Array.isArray(responseData) ? responseData : []);
};

/************************************************
 * 인력 상황판 테이블 컬럼 세팅용(좌측 영역)
 * activeTable 상태에 따라 보여줄 컬럼 목록 관리
 ************************************************/
const tableConfigs: Record<string, { title: string; cols: string[] }> = {
    total: { 
        title: '총 인원',
        cols: ['dept', 'total', 'project', 'waiting', 'vacation']
    },
    waiting: {
        title: '본사 대기인원',
        cols: ['dept', 'waiting', 'waitingVacation']
    },
    vacation: {
        title: '휴가자',
        cols: ['dept', 'vacationProject', 'vacationWaiting', 'vacation']
    },
};

/************************************************
 * 도넛차트 설정
 ************************************************/
const DonutChart = ({ data, size, title = "총 인원" }: { data: any[], size: number, title?: string | string[] }) => {
    const radius = size * 0.35;
    const centerX = size / 2;
    const centerY = size / 2;
    const circumference = 2 * Math.PI * radius;
    let cumulativePercent = 0;
    
    const totalVal = data.reduce((acc, cur) => acc + cur.value, 0);
    const titleLines = Array.isArray(title) ? title : [title];

    return (
        <div style={{ textAlign: 'center' }}>
            <svg width={size} height={size}>
                <circle 
                    cx={centerX} 
                    cy={centerY} 
                    r={radius} 
                    fill="none" 
                    stroke="#f0f0f0" 
                    strokeWidth={20} 
                />
                {data.map((item, idx) => {
                    if (totalVal === 0) return null;
                    const percent = (item.value / totalVal) * 100;
                    const strokeLength = (percent * circumference) / 100;
                    const offset = (cumulativePercent * circumference) / 100;
                    cumulativePercent += percent;

                    return (
                        <circle
                            key={idx} 
                            cx={centerX} 
                            cy={centerY} 
                            r={radius} 
                            fill="none"
                            stroke={item.color} 
                            strokeWidth={20}
                            strokeDasharray={`${strokeLength} ${circumference}`} 
                            strokeDashoffset={-offset}
                            transform={`rotate(-90 ${centerX} ${centerY})`}                // 그래프 시작지점 설정(12시부터 시작하도록 90도 돌려서 설정)
                            style={{ transition: 'stroke-dashoffset 0.5s ease-in-out' }}
                        />
                    );
                })}
                {/* 도넛차트 가운데 텍스트 설정 */}
                <text x={centerX} y={centerY} textAnchor="middle" style={{ fontWeight: 'bold' }}>
                    {titleLines.map((line, idx) => (
                        <tspan 
                            key={idx} 
                            x={centerX} 
                            dy={idx === 0 ? `-${(titleLines.length - 1) * 0.5}em` : '1.2em'} 
                            fontSize="12px"
                        >
                            {line}
                        </tspan>
                    ))}
                    <tspan x={centerX} dy="1.4em" fontSize="14px">{totalVal}</tspan>
                </text>
            </svg>
        </div>
    );
};

const Usr0401m = () => {
    /************************************************
     * 화면 초기화 영역  
     ************************************************/
    const dispatch = useAppDispatch();
    
    const { 
        data, 
        activeTable, 
        showDeptUserList, 
        selectedDept, 
        deptUserList, 
        projectList, 
        projectSummary, 
        listFilter, 
        projectListFilter,
        bankProjects, 
        projectDetailList 
    } = useAppSelector((state) => state.Usr0401m);

    // 모달 닫기 이벤트
    const handleCloseModal = useCallback(() => {
        dispatch(Usr0401mAction.setShowDeptUserList(false));
    }, [dispatch]);

    // 부서별 통계 트리구조 조회(좌측 인력 상황판 총 인원/본사 대기인원/휴가자 클릭시)
    const fn_getDeptTree = useCallback(() => {
        commonProxy.callAsyncSvc({}, {
            txCd: 'TRUSRR0035',
            success: (res: any) => {
                dispatch(Usr0401mAction.setData(convertTreeToTable(res.deptTree || [])));
            },
        });
    }, [dispatch]);

    // 부서별 상세 명단 조회(총 인원/본사 대기인원/휴가자 모달에 나올 데이터)
    const fn_getDeptUserList = useCallback((deptCd: string, deptName: string) => {
        commonProxy.callAsyncSvc({ deptCd, mode: 'DETAIL' }, {
            txCd: 'TRUSRR0035',
            success: (res: any) => {
                dispatch(Usr0401mAction.setDeptUserList(extractUserList(res)));
                dispatch(Usr0401mAction.setSelectedDept({ dept: deptName, key: deptCd }));
                dispatch(Usr0401mAction.setShowDeptUserList(true));
            }
        });
    }, [dispatch]);

    // 프로젝트 투입현황 목록 조회(좌측 인력 상황판 프로젝트 투입인원 클릭시)
    const fn_getProjectList = useCallback(() => {
        commonProxy.callAsyncSvc({ mode: 'PROJECT' }, {
            txCd: 'TRUSRR0035',
            success: (res: any) => {
                dispatch(Usr0401mAction.setProjectList(res.projectList || res || []));
            },
        });
    }, [dispatch]);

    // 프로젝트별 상세 명단 조회(프로젝트 투입인원 모달에 나올 데이터)
    const fn_getProjectUserList = useCallback((projectCd: string, projectNm: string) => {
        commonProxy.callAsyncSvc({ projectCd, mode: 'PROJECT_DETAIL' }, {
            txCd: 'TRUSRR0035',
            success: (res: any) => {
                dispatch(Usr0401mAction.setDeptUserList(extractUserList(res)));
                dispatch(Usr0401mAction.setSelectedDept({ dept: projectNm, key: projectCd }));
                dispatch(Usr0401mAction.setShowDeptUserList(true));
            }
        });
    }, [dispatch]);

    // 프로젝트 상황판 데이터 조회(우측 프로젝트 상황판)
    const fn_getProjectDashboard = useCallback(() => {

        // 최상단 프로젝트 상황판 요약 데이터
        commonProxy.callAsyncSvc({ mode: 'SUMMARY' }, {
            txCd: 'TRPJTR0010',
            success: (res: any) => {
                const summary = res.summary || {};
                dispatch(Usr0401mAction.setProjectSummary({
                    total: summary.TOTAL_COUNT || 0,
                    completed: summary.COMPLETED_COUNT || 0,
                    inProgress: summary.IN_PROGRESS_COUNT || 0,
                    waiting: summary.WAITING_COUNT || 0  
                }));
            }
        });

        // 프로젝트 상세 목록 및 은행별 차트 데이터
        commonProxy.callAsyncSvc({ mode: 'LIST' }, {
            txCd: 'TRPJTR0010',
            success: (res: any) => {
                const list = res.projectList || [];
                dispatch(Usr0401mAction.setProjectDetailList(list)); 
                
                // 은행/사이트(BANK_NM) 기준으로 데이터 그룹핑(막대 차트)
                const groupedBankData = list.reduce((acc: any, curr: any) => {
                    const bankNm = curr.BANK_NM || '기타';
                    if (!acc[bankNm]) {
                        acc[bankNm] = { bankNm, color: '#1890ff', projects: [] };
                    }
                    
                    acc[bankNm].projects.push({
                        projectNm: curr.PROJECT_NM,
                        count: curr.MANPOWER_COUNT || 0,
                        status:  curr.STATUS === '완료' ? 'COMPLETED' :
                                 curr.STATUS === '대기' ? 'WAITING' :
                                 'IN_PROGRESS'
                    });
                    return acc;
                }, {});

                dispatch(Usr0401mAction.setBankProjects(Object.values(groupedBankData))); 
            }
        });
    }, [dispatch]);

    // 인력 상황판 항목(총 인원/프로젝트 투입인원/본사 대기인원/휴가자) 클릭 시 테이블 전환
    const columnClick = useCallback((type: string) => {
        dispatch(Usr0401mAction.setActiveTable(type));
        dispatch(Usr0401mAction.setShowDeptUserList(false));
        if (type === 'project') fn_getProjectList();
    }, [dispatch, fn_getProjectList]);

    // 프로젝트 상황판 항목(총 프로젝트 수/완료 프로젝트/진행중 프로젝트/대기중 프로젝트) 클릭 시 테이블 전환
    const projectColumnClick = useCallback((type: string) => {
        dispatch(Usr0401mAction.setProjectListFilter(type))
    }, [dispatch, fn_getProjectList]);

    useEffect(() => { 
        fn_getDeptTree(); 
        fn_getProjectDashboard();
    }, [fn_getDeptTree, fn_getProjectDashboard]);

    /************************************************
     * 데이터 계산 영역
     ************************************************/
    // 우측 차트용 데이터 계산(진행중/완료 프로젝트 개수 및 투입 인원)
    const { activeBanks, donutChartData, maxProjectCount } = useMemo(() => {
        const banks = bankProjects.filter(bank => bank.projects && bank.projects.length > 0);
        let inProgress = 0;
        let completed  = 0;
        let waiting    = 0; //대기
        
        banks.forEach(bank => {
            bank.projects.forEach(proj => {
                if (proj.status === 'COMPLETED') {
                    completed++;
                } else if (proj.status === 'WAITING') {
                    waiting++;
                } else {
                    inProgress++;
                }
            });
        });

        // 프로젝트 투입인원 최대값 계산(막대 그래프 최대 높이 기준)
        const maxCount = Math.max(...banks.flatMap(bank => bank.projects.map((p: any) => p.count)), 1);

        return {
            activeBanks: banks,
            donutChartData: [
                // 진행중, 완료, 대기 표시 색상 설정
                { label: '진행중', value: inProgress, color: '#faad14' },
                { label: '완료', value: completed, color: '#52c41a' },
                { label: '대기', value: waiting, color: '#bfbfbf' }
            ],
            maxProjectCount: maxCount
        };
    }, [bankProjects]);

    // 모달창 상세 명단 필터링 처리
    // 클릭한 헤더의 listFilter 속성값에 맞춰서 조건에 맞는 사용자 추출
    const filteredUserList = useMemo(() => {
        if (!deptUserList) return [];
        switch (listFilter) {
            case 'project': 
                return deptUserList.filter(user => user.PROJECT_YN === 'Y');
            case 'waiting': 
                return deptUserList.filter(user => user.PROJECT_YN === 'N' || !user.PROJECT_YN);
            case 'vacation': 
                return deptUserList.filter(user => user.VACATION_YN === 'Y');
            case 'waitingVacation': 
                return deptUserList.filter(user => (user.PROJECT_YN === 'N' || !user.PROJECT_YN) && user.VACATION_YN === 'Y');
            case 'projectVacation': 
                return deptUserList.filter(user => user.PROJECT_YN === 'Y' && user.VACATION_YN === 'Y');
            case 'vacationProject': 
                return deptUserList.filter(user => user.VACATION_YN === 'Y' && user.PROJECT_YN === 'Y');
            case 'vacationWaiting': 
                return deptUserList.filter(user => user.VACATION_YN === 'Y' && (user.PROJECT_YN === 'N' || !user.PROJECT_YN));
            default: 
                return deptUserList;
        }
    }, [deptUserList, listFilter]);

    // 프로젝트 상세 필터링
    const STATUS_MAP: Record<string, string> = {
        completed: '완료',
        inProgress: '진행',
        waiting: '대기',
    };

    const filteredProjectList = useMemo(() => {
        if (!projectListFilter || projectListFilter === 'total') {
            return projectDetailList;
        }

        const targetStatus = STATUS_MAP[projectListFilter];
        return projectDetailList.filter((item: ProjectDataType) => item.STATUS === targetStatus);

    }, [projectDetailList, projectListFilter]);


    /************************************************
     * 공통 컬럼 정의
     ************************************************/
    // 인력 상황판 공통 컬럼 생성 및 클릭 이벤트 설정
    const createOverviewCol = (title: string, type: string, unit = '명') => ({
        title: () => (
            <span style={{ cursor: 'pointer' }} onClick={() => columnClick(type)}>
                {title}
            </span>
        ),
        dataIndex: type, 
        align: 'center' as const, 
        width: '25%',
        render: (text: number) => (
            <span 
                style={{ cursor: 'pointer', display: 'block', width: '100%' }} 
                onClick={() => columnClick(type)}
            >
                {text !== undefined ? `${text} ${unit}` : '-'}
            </span>
        ),
    });

    // 프로젝트 상황판 공통 컬럼 생성 및 클릭 이벤트 설정
    const createProjectOverviewCol = (title: string, type: string, unit = '건') => ({
        title: () => (
            <span style={{ cursor: 'pointer' }} onClick={() => projectColumnClick(type)}>
                {title}
            </span>
        ),
        dataIndex: type, 
        align: 'center' as const, 
        width: '25%',
        render: (text: number) => (
            <span 
                style={{ cursor: 'pointer', display: 'block', width: '100%' }} 
                onClick={() => projectColumnClick(type)}
            >
                {text !== undefined ? `${text} ${unit}` : '-'}
            </span>
        ),
    });

    // 총 인원/본사 대기인원/휴가자 테이블 컬럼 생성 및 모달 팝업 이벤트 설정
    const createBaseCol = (title: string | React.ReactNode, dataIndex: string, width: string) => ({
        title, 
        dataIndex, 
        key: dataIndex, 
        align: 'center' as const, 
        width,
        render: (text: number, record: DeptDataType) => (
            <span style={{ cursor: 'pointer' }} onClick={() => {
                dispatch(Usr0401mAction.setListFilter(dataIndex));
                fn_getDeptUserList(record.key, record.dept);
            }}>
                {text !== undefined ? `${text} 명` : '-'}
            </span>
        )
    });

    // 인원 상황판 데이터 매핑
    const overviewData = useMemo<DeptDataType[]>(() => {
        return data.map((item, idx): DeptDataType => ({
            ...item, 
            children: undefined,
            key: `summary_${item.key}_${idx}` 
        }));
    }, [data]);

    const overviewColumns: ColumnsType<DeptDataType> = useMemo(() => [
        createOverviewCol('총 인원', 'total'),
        createOverviewCol('프로젝트 투입인원', 'project'),
        createOverviewCol('본사 대기인원', 'waiting'),
        createOverviewCol('휴가자', 'vacation'),
    ], [columnClick]);

    // 프로젝트 상황판 데이터 매핑
    const overviewProjectData = useMemo<ProjectSummaryType[]>(() => {
        return projectSummary ? [{ ...projectSummary, key: '1' }] : [];
    }, [projectSummary]);

    const overviewProjectColumns: ColumnsType<ProjectSummaryType> = useMemo(() => [
        createProjectOverviewCol('총 프로젝트 수', 'total'),
        createProjectOverviewCol('완료 프로젝트', 'completed'),
        createProjectOverviewCol('진행중 프로젝트', 'inProgress'),
        createProjectOverviewCol('대기중 프로젝트', 'waiting'),
    ], [projectColumnClick]);

    const baseColumns: ColumnsType<DeptDataType> = useMemo(() => [
        {
            title: () => <div style={{ textAlign: 'center' }}>부서</div>, 
            dataIndex: 'dept', 
            key: 'dept', 
            align: 'left', 
            width: '40%',
            render: (text: string, record: DeptDataType) => (
                <div 
                    style={{ marginLeft: record.depth * 30, display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                    onClick={() => {
                        dispatch(Usr0401mAction.setListFilter(activeTable || 'total'));
                        fn_getDeptUserList(record.key, record.dept);
                    }}
                >
                    {record.depth > 0 && <span style={{ marginRight: 3 }}>┗</span>}
                    <span style={{ letterSpacing: '-0.5px' }}>{text}</span>
                </div>
            )
        },
        createBaseCol('총 인원', 'total', '15%'),
        createBaseCol(<span style={{ fontSize: '12px', letterSpacing: '-1.5px' }}>프로젝트 투입인원</span>, 'project', '15%'),
        createBaseCol('본사 대기인원', 'waiting', '15%'),
        createBaseCol('휴가자', 'vacation', '15%'),
        createBaseCol(<span style={{ fontSize: '13px' }}>본사 대기인원 중 휴가자</span>, 'waitingVacation', '20%'),
        createBaseCol('프로젝트 투입인원 중 휴가자', 'projectVacation', '15%'),
        createBaseCol(<span style={{ fontSize: '12px', letterSpacing: '-1px' }}>휴가자 중 프로젝트 투입인원</span>, 'vacationProject', '21%'),
        createBaseCol(<span style={{ fontSize: '13px' }}>휴가자 중 본사 대기인원</span>, 'vacationWaiting', '21%'),
    ], [fn_getDeptUserList, activeTable, dispatch]);

    // 프로젝트 투입현황 테이블 컬럼
    const projectColumns: ColumnsType<ProjectDataType> = useMemo(() => {
        // 프로젝트명 클릭시 해당 프로젝트 코드 기준으로 투입인원 목록 조회
        const renderProjectCell = (filterType: string) => (text: string, record: ProjectDataType) => (
            <span style={{ cursor: 'pointer' }} onClick={() => {
                dispatch(Usr0401mAction.setListFilter(filterType)); 
                fn_getProjectUserList(record.PROJECT_CD, record.PROJECT_NM);
            }}>
                {text !== undefined ? `${text} 명` : '-'}
            </span>
        );

        return [
            {
                title: '프로젝트명', 
                dataIndex: 'PROJECT_NM', 
                key: 'PROJECT_NM', 
                align: 'center', 
                width: '50%',
                render: (text: string, record: ProjectDataType) => {
                    // 글자가 너무 길 경우 ... 으로 처리
                    const isLong = text && text.length > 28;
                    return (
                        <Tooltip title={isLong ? text : ''}>
                            <span style={{ cursor: 'pointer' }} onClick={() => {
                                dispatch(Usr0401mAction.setListFilter('total'));
                                fn_getProjectUserList(record.PROJECT_CD, record.PROJECT_NM);
                            }}>
                                {isLong ? `${text.substring(0, 28)}...` : text}
                            </span>
                        </Tooltip>
                    );
                }
            },
            { 
                title: '프로젝트 투입인원', 
                dataIndex: 'PJT_COUNT', 
                key: 'PJT_COUNT', 
                align: 'center', 
                width: '25%', 
                render: renderProjectCell('project') 
            },
            { 
                title: <span style={{ fontSize: '13px', whiteSpace: 'nowrap', letterSpacing: '-0.5px' }}>프로젝트 투입인원 중 휴가자</span>, 
                dataIndex: 'PJT_VACATION_COUNT', 
                key: 'PJT_VACATION_COUNT', 
                align: 'center', 
                width: '25%', 
                render: renderProjectCell('projectVacation') 
            }
        ];
    }, [fn_getProjectUserList, dispatch]);

    /************************************************
     * 모달 타이틀 설정
     * 사용자가 클릭한 조건(listFilter) 에 따라 타이틀 텍스트 동적 변경
     ************************************************/
    const getModalTitle = () => {
        const titleMap: Record<string, string> = {
            total: activeTable === 'project' ? ' 프로젝트 투입인원' : ' 전체 인원',
            project: ' 프로젝트 투입인원', 
            waiting: ' 본사 대기인원', 
            vacation: ' 휴가자',
            waitingVacation: ' 본사 대기인원 중 휴가자', 
            projectVacation: ' 프로젝트 투입인원 중 휴가자',
            vacationProject: ' 휴가자 중 프로젝트 투입인원', 
            vacationWaiting: ' 휴가자 중 본사 대기인원',
        };
        return `${selectedDept?.dept || ''}${titleMap[listFilter] || ''} 목록`;
    };

    /************************************************
     * 프로젝트 상환판 도넛 차트 텍스트 출력용
     ************************************************/
    const inProgressCount = donutChartData.find(d => d.label === '진행중')?.value || 0;
    const completedCount = donutChartData.find(d => d.label === '완료')?.value || 0;
    const waitingCount = donutChartData.find(d => d.label === '대기')?.value || 0;  //대기추가
    const totalProjCount = inProgressCount + completedCount + waitingCount;

    /************************************************
     * 화면 렌더링 영역
     ************************************************/
    return (
        <Layout>
            {/* 하단 표 영역 고정 높이를 가지게 CSS 적용(04-07 추가) */}
            <style>
                {`
                .custom-fixed-table .ant-table-container {
                    border: 1px solid #f0f0f0 !important;
                    border-radius: 8px !important;
                    overflow: hidden !important; 
                    background-color: #fff;
                }
                .custom-fixed-table .ant-table-body {
                    height: 360px !important;
                    overflow-y: auto !important;
                }
                .custom-fixed-table .ant-table {
                    border: none !important;
                }
                `}
            </style>

            <Content style={{ padding: '5px' }}>
                <Row gutter={[10, 10]} align="top">
                    
                    {/* 좌측 영역(인력 상황판) */}
                    <Col span={10}>
                        <div style={{ marginBottom: 20 }}>
                            <Space style={{ marginBottom: 5 }}>
                                <img className='title-icon' alt='' />
                                <Text strong>
                                    인력 상황판 <span style={{ fontSize: '13px', color: '#888' }}>(오늘기준)</span>
                                </Text>
                            </Space>
                            <Table<DeptDataType>
                                dataSource={overviewData} 
                                columns={overviewColumns} 
                                pagination={false} 
                                bordered 
                                rowKey="key" 
                                size="small"
                                summary={(pageData) => {
                                    if (pageData.length === 0) return null;
                                    const { total = 0, project = 0, waiting = 0 } = pageData[0];
                                    return (
                                        <Table.Summary fixed>
                                            <Table.Summary.Row>
                                                <Table.Summary.Cell index={0} colSpan={4}>
                                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '180px', gap: '30px' }}>
                                                        <DonutChart 
                                                            data={[
                                                                { value: project, color: '#52c41a' }, 
                                                                { value: waiting, color: '#faad14' }
                                                            ]} 
                                                            size={160} 
                                                        />
                                                        <div>
                                                            <div>
                                                                <span style={{ color: '#52c41a' }}>●</span> 프로젝트 투입인원: <strong>{project}명 </strong> 
                                                                ({total > 0 ? Math.round((project/total)*100) : 0}%)
                                                            </div>
                                                            <div>
                                                                <span style={{ color: '#faad14' }}>●</span> 본사 대기인원: <strong>{waiting}명 </strong> 
                                                                ({total > 0 ? Math.round((waiting/total)*100) : 0}%)
                                                            </div>
                                                        </div>
                                                    </div>
                                                </Table.Summary.Cell>
                                            </Table.Summary.Row>
                                        </Table.Summary>
                                    );
                                }}
                            />
                        </div>

                        {/* 총 인원/본사 대기인원/휴가자 탭 클릭 시 테이블 표시 */}
                        {activeTable && activeTable !== 'project' && tableConfigs[activeTable] && (
                            <div style={{ marginTop: 10 }}>
                                <Space style={{ marginBottom: 5 }}>
                                    <img className='title-icon' alt='' />
                                    <Text strong>{tableConfigs[activeTable].title}</Text>
                                </Space>
                                <Table<DeptDataType> 
                                    className="custom-fixed-table"
                                    dataSource={data} 
                                    columns={baseColumns.filter((col: any) => col.key && tableConfigs[activeTable!].cols.includes(col.key))} 
                                    pagination={false} 
                                    bordered 
                                    rowKey="key" 
                                    size="small" 
                                    scroll={{ y: 360 }} 
                                />
                            </div>
                        )}
                        
                        {/* 프로젝트 탭 클릭 시 테이블 표시 */}
                        {activeTable === 'project' && (
                            <div style={{ marginTop: 10 }}>
                                <Space style={{ marginBottom: 5 }}>
                                    <img className='title-icon' alt='' />
                                    <Text strong>프로젝트 투입현황</Text>
                                </Space>
                                <Table<ProjectDataType> 
                                    className="custom-fixed-table"
                                    dataSource={projectList} 
                                    columns={projectColumns} 
                                    pagination={{ 
                                        showTotal: (total) => `총 ${total} 건`, 
                                        defaultPageSize: 10 
                                    }} 
                                    bordered 
                                    rowKey="PROJECT_CD" 
                                    size="small" 
                                    scroll={{ y: 360 }} 
                                />
                            </div>
                        )}
                    </Col>
                    
                    {/* 우측 영역(프로젝트 상황판) */}
                    <Col span={14}>
                        <div style={{ marginBottom: 20 }}>
                            <Space style={{ marginBottom: 5 }}>
                                <img className='title-icon' alt='' />
                                <Text strong>
                                {/* TODO - 테스트용으로 임시로 2025년 기준으로 바꿔놓음 나중에 쿼리 수정하기@@@ */}
                                    프로젝트 상황판 <span style={{ fontSize: '13px', color: '#888' }}>(올해기준)</span>
                                </Text>
                            </Space>

                            {/* 프로젝트 건수 요약 영역 */}
                            <Table<ProjectSummaryType>
                                dataSource={overviewProjectData} 
                                columns={overviewProjectColumns} 
                                pagination={false} 
                                bordered 
                                size="small" 
                                rowKey="key" 
                            />

                            {/* 그래프 영역 */}
                            <div style={{ 
                                border: '1px solid #f0f0f0', 
                                borderTop: 'none', 
                                padding: '10px 5px', 
                                display: 'flex', 
                                alignItems: 'center', 
                                height: '197px', 
                                boxSizing: 'border-box', 
                                backgroundColor: '#fff' 
                            }}>
                                {/* 도넛 차트 영역 (진행 상태별 통계) */}
                                <div style={{ flex: 0.65, display: 'flex', justifyContent: 'center', height: '100%' }}>
                                    <DonutChart data={donutChartData} size={160} title={'총 프로젝트'} />
                                    <div>
                                        <div>
                                            <span style={{ color: '#52c41a' }}>●</span> 완료: <strong>{completedCount}건 </strong>
                                            ({totalProjCount > 0 ? Math.round((completedCount/totalProjCount)*100) : 0}%)
                                        </div>
                                        <div>
                                            <span style={{ color: '#faad14' }}>●</span> 진행중: <strong>{inProgressCount}건 </strong>
                                            ({totalProjCount > 0 ? Math.round((inProgressCount/totalProjCount)*100) : 0}%)
                                        </div>
                                        <div>
                                            <span style={{ color: '#bfbfbf' }}>●</span> 대기: <strong>{waitingCount}건 </strong>
                                            ({totalProjCount > 0 ? Math.round((waitingCount/totalProjCount)*100) : 0}%)
                                        </div>
                                    </div>
                                </div>
                                {/* 막대 차트 영역 (은행/사이트별 인원 투입 현황) */}
                                <div style={{ flex: 1.35, height: '100%', overflow: 'hidden' }}>
                                    <div style={{ display: 'flex', overflowX: 'auto', overflowY: 'hidden', width: '100%', height: '100%', paddingBottom: '10px' }}>
                                        {activeBanks.map((bank, bIdx) => (
                                            <div key={bIdx} style={{ display: 'flex', flexDirection: 'column', flexShrink: 0, width: '80px' }}>
                                                <div style={{ 
                                                    display: 'flex', 
                                                    alignItems: 'flex-end', 
                                                    justifyContent: 'center', 
                                                    height: '140px', 
                                                    borderBottom: '1px solid #333', 
                                                    borderRight: bIdx !== activeBanks.length - 1 ? '1px dashed #e8e8e8' : 'none', 
                                                    padding: '0 5px', 
                                                    gap: '4px' 
                                                }}>
                                                    {bank.projects.map((proj: any, pIdx: number) => {
                                                        // 투입 인원 최댓값 기준 막대 그래프 높이 설정
                                                        const barHeight = (proj.count / maxProjectCount) * 105;
                                                        return (
                                                            <Tooltip key={pIdx} title={`${proj.projectNm} (${proj.count}명)`}>
                                                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer' }}>
                                                                    <span style={{ fontSize: '11px', fontWeight: 'bold', marginBottom: '4px' }}>
                                                                        {proj.count}
                                                                    </span>
                                                                    <div style={{ 
                                                                        width: '8px', 
                                                                        height: `${barHeight}px`, 
                                                                        backgroundColor: bank.color, 
                                                                        borderRadius: '2px 2px 0 0', 
                                                                        transition: 'height 0.3s ease' 
                                                                    }}></div>
                                                                </div>
                                                            </Tooltip>
                                                        );
                                                    })}
                                                </div>
                                                <div style={{ fontSize: '10px', fontWeight: 'bold', textAlign: 'center', marginTop: '8px' }}>
                                                    {bank.bankNm}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* 프로젝트 상세 테이블 영역 */}
                        <div style={{ marginTop: 10 }}>
                            <Space style={{ marginBottom: 5 }}>
                                <img className='title-icon' alt='' />
                                <Text strong>프로젝트 상세</Text>
                            </Space>
                            <Table
                                className="custom-fixed-table"
                                dataSource={filteredProjectList}
                                columns={[
                                    { 
                                        title: '프로젝트명', 
                                        dataIndex: 'PROJECT_NM', 
                                        key: 'PROJECT_NM', 
                                        align: 'center', 
                                        width: '32%', 
                                        render: (text: string, record: ProjectDataType) => {
                                            if (!text) return '-';
                                            const isLong = text.length > 24;
                                            return (
                                                <Tooltip title={isLong ? text : ''}>
                                                    <span 
                                                        style={{ cursor: 'pointer' }} 
                                                        onClick={() => {
                                                            dispatch(Usr0401mAction.setListFilter('total'));
                                                            fn_getProjectUserList(record.PROJECT_CD, record.PROJECT_NM);
                                                        }}
                                                    >
                                                        {isLong ? `${text.substring(0, 24)}...` : text}
                                                    </span>
                                                </Tooltip>
                                            );
                                        }

                                    },
                                    {
                                        title: <span style={{ fontSize: '13px' }}>프로젝트 시작일</span>, 
                                        dataIndex: 'START_DT', 
                                        key: 'START_DT', 
                                        align: 'center', 
                                        width: '11%', 
                                        render: (text) => formatDate(text) 
                                    },
                                    { 
                                        title: <span style={{ fontSize: '13px' }}>프로젝트 종료일</span>, 
                                        dataIndex: 'END_DT', 
                                        key: 'END_DT', 
                                        align: 'center', 
                                        width: '11%', 
                                        render: (text) => formatDate(text) 
                                    },
                                    {
                                        title: '총인원수', 
                                        dataIndex: 'MANPOWER_COUNT', 
                                        key: 'MANPOWER_COUNT', 
                                        align: 'center', 
                                        width: '8%', 
                                        render: (val) => val !== undefined ? `${val} 명` : '-' 
                                    },
                                    {
                                        title: '직원수', 
                                        dataIndex: 'EMP_COUNT', 
                                        key: 'EMP_COUNT', 
                                        align: 'center', 
                                        width: '6%', 
                                        render: (val) => val !== undefined ? `${val} 명` : '-' 
                                    },
                                    {
                                        title: '외주인력수', 
                                        dataIndex: 'EXT_EMP_COUNT', 
                                        key: 'EXT_EMP_COUNT', 
                                        align: 'center', 
                                        width: '9%', 
                                        render: (val) => val !== undefined ? `${val} 명` : '-' 
                                    },
                                    {
                                        title: 'PM', 
                                        dataIndex: 'PM_NM', 
                                        key: 'PM_NM', 
                                        align: 'center', 
                                        width: '8%', 
                                        render: (val) => val || '-' 
                                    },
                                    { 
                                        title: '은행/사이트', 
                                        dataIndex: 'BANK_NM', 
                                        key: 'BANK_NM', 
                                        align: 'center', 
                                        width: '18%', 
                                        render: (val) => val || '-' 
                                    },
                                    { 
                                        title: '상태', 
                                        dataIndex: 'STATUS', 
                                        key: 'STATUS', 
                                        align: 'center', 
                                        width: '8%' 
                                    },
                                ]}
                                pagination={{ 
                                    showTotal: (total) => `총 ${total} 건`, 
                                    defaultPageSize: 20,
                                    showSizeChanger: true
                                }} 
                                bordered 
                                rowKey={(record, index) => record.PROJECT_CD || `pjt_detail_${index}`} 
                                size="small" 
                                scroll={{ y: 360 }}
                            />
                        </div>
                    </Col>
                </Row>
                
                {/* 모달 영역 */}
                <Modal 
                    title={<Text strong>{getModalTitle()}</Text>} 
                    open={showDeptUserList} 
                    onCancel={handleCloseModal} 
                    footer={(
                        <div style={{ textAlign: 'center' }}>
                            <Button key="close" className="background-color-default-button" onClick={handleCloseModal}>
                                닫기
                            </Button>
                        </div>
                    )} 
                    width={1200} 
                    centered 
                    destroyOnClose
                >
                    <Table<UserDataType>
                        dataSource={filteredUserList}
                        columns={[
                            { title: '부서', dataIndex: 'DEPT_NM', align: 'center', width: '18%' },
                            { title: '직위', dataIndex: 'POST_NM', align: 'center', width: '10%' },
                            { title: '이름', dataIndex: 'USER_NM', align: 'center', width: '10%' },
                            ...(activeTable !== 'project' ? [{ 
                                title: '프로젝트명', 
                                dataIndex: 'PROJECT_NM', 
                                align: 'center', 
                                width: '30%', 
                                render: (text: string) => {
                                    if (!text) return '-';
                                    return text.length > 28 ? (
                                        <Tooltip title={text}>
                                            <span>{text.substring(0, 28)}...</span>
                                        </Tooltip>
                                    ) : text;
                                }
                            } as ColumnsType<UserDataType>[number]] : []),
                            { title: '휴가여부', dataIndex: 'VACATION_YN', align: 'center', width: '8%' },
                            { 
                                title: '휴가 시작일', 
                                dataIndex: 'OFF_ST_DT', 
                                align: 'center', 
                                width: '12%', 
                                render: (text) => formatDate(text) 
                            },
                            { 
                                title: '휴가 종료일', 
                                dataIndex: 'OFF_ED_DT', 
                                align: 'center', 
                                width: '12%', 
                                render: (text) => formatDate(text) 
                            },
                        ]}
                        bordered 
                        pagination={{ 
                            showTotal: (total) => `총 ${total} 건`, 
                            defaultPageSize: 10 
                        }} 
                        rowKey={(record, index) => `${record.EMP_NO}_${index}`} 
                        size="small"
                    />
                </Modal>
            </Content>
        </Layout>
    );
};

export default Usr0401m;